import React, { useEffect, useState } from "react";

import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import ProjectLanguageSelector from "./ProjectLanguageSelector";
import languageCodeList from "../../../constants/languages";
import { navigatorLanguage } from "../../../i18n";
import {
  addProjectLanguage,
  removeProjectLanguage,
  resolveImageValue,
  seedDefaultLanguage,
  setDefaultProjectLanguage,
} from "../../../utils/projectTranslations";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import SimpleImageUpload from "../../utils/SimpleImageUpload";
import { getPageLinkTranslation } from "../../utils/Translations";

import type { ChangeEvent, Dispatch, JSX, SetStateAction } from "react";

import type {
  LanguageCode,
  ProjectTranslation,
  UpdatedProject,
  UpdatedProjectTranslation,
} from "../../../api/ProjectAPI.types";

const MAX_PAGE_LINKS = 5;

const DEFAULT_NEW_LANGUAGE: LanguageCode = (
  languageCodeList as readonly string[]
).includes(navigatorLanguage)
  ? (navigatorLanguage as LanguageCode)
  : "en";

type ImageAttr = "logo" | "favicon" | "cover_picture_16_6";

interface ImageFieldConfig {
  attr: ImageAttr;
  format: "16_9" | "1_1" | "16_6";
  labelKey: string;
  getImageUrl: (
    translation: ProjectTranslation | UpdatedProjectTranslation,
  ) => string | null | undefined;
}

const IMAGE_FIELDS: ImageFieldConfig[] = [
  {
    attr: "logo",
    format: "16_9",
    labelKey: "logo",
    getImageUrl: (translation) => translation.logo_url,
  },
  {
    attr: "favicon",
    format: "1_1",
    labelKey: "favicon",
    getImageUrl: (translation) => translation.favicon_url,
  },
  {
    attr: "cover_picture_16_6",
    format: "16_6",
    labelKey: "coverPicture",
    getImageUrl: (translation) => translation.pictures?.cover_picture_16_6?.low,
  },
];

function PageLinkField({
  id,
  label,
  value,
  className,
  onChange,
  action,
}: {
  id: string;
  label: string;
  value: string;
  className?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ""}`}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-muted-foreground text-xs" htmlFor={id}>
          {label}
        </Label>
        {action}
      </div>
      <Input
        className="bg-card"
        id={id}
        onChange={onChange}
        placeholder={label}
        value={value}
      />
    </div>
  );
}

function LabeledField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.25">
      <label className="text-foreground font-semibold" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}

interface ProjectAdminTranslationsProps {
  project: UpdatedProject;
  setProject: Dispatch<SetStateAction<UpdatedProject>>;
  onSubmit: (project: UpdatedProject, skipValidation?: boolean) => void;
  itemIsValid: (project: UpdatedProject) => string[];
  onChangeTranslation: (
    key: keyof UpdatedProjectTranslation,
    translation: ProjectTranslation | UpdatedProjectTranslation,
    target: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>["target"],
  ) => void;
}

function ProjectAdminTranslations({
  project,
  setProject,
  onSubmit,
  itemIsValid,
  onChangeTranslation,
}: ProjectAdminTranslationsProps): JSX.Element | null {
  const { t } = useTranslation("editor-project");
  const [activeIndex, setActiveIndex] = useState(0);

  const activeLanguageIndex = Math.min(
    activeIndex,
    Math.max(project.translations.length - 1, 0),
  );
  const activeTranslation = project.translations[activeLanguageIndex];

  const errorMessageList = itemIsValid(project);
  const addDisabledTooltip =
    errorMessageList.length > 0 ? errorMessageList.join(", ") : null;

  useEffect(() => {
    if (project.translations.length === 0) {
      setProject((prev) => seedDefaultLanguage(prev, DEFAULT_NEW_LANGUAGE));
    }
  }, [project.translations.length, setProject]);

  if (!activeTranslation) return null;

  const activeLanguage = activeTranslation.language;

  const updateActiveTranslation = (
    patch: Partial<UpdatedProjectTranslation>,
  ): void => {
    setProject((prev) => ({
      ...prev,
      translations: prev.translations.map((translation, index) =>
        index === activeLanguageIndex
          ? { ...translation, ...patch }
          : translation,
      ),
    }));
  };

  const handleDeleteImage = (attr: ImageAttr): void =>
    updateActiveTranslation({ [attr]: null });

  const handleAddLanguage = (languageCode: LanguageCode): void => {
    const updated = addProjectLanguage(project, languageCode);
    setProject(updated);
    setActiveIndex(updated.translations.length - 1);
    onSubmit(updated, true);
  };

  const handleRemoveLanguage = (languageCode: LanguageCode): void => {
    const updated = removeProjectLanguage(project, languageCode);
    setProject(updated);
    setActiveIndex(updated.translations_indexes.default.true[0] ?? 0);
  };

  const handleSetDefault = (languageCode: LanguageCode): void => {
    setProject(setDefaultProjectLanguage(project, languageCode));
  };

  const handleChangePageLink = (
    attr: "title" | "url",
    linkIndex: number,
    value: string,
  ): void => {
    setProject((prev) => ({
      ...prev,
      page_links: prev.page_links.map((pageLink, index) => {
        if (index !== linkIndex) return pageLink;

        const translations = pageLink.translations.map((translation) =>
          translation.language === activeLanguage
            ? { ...translation, [attr]: value }
            : translation,
        );

        return { ...pageLink, translations };
      }),
    }));
  };

  const addPageLink = (): void => {
    setProject((prev) => ({
      ...prev,
      page_links: [...prev.page_links, { translations: [] }],
    }));
  };

  const deletePageLink = (linkIndex: number): void => {
    setProject((prev) => ({
      ...prev,
      page_links: prev.page_links.filter((_, index) => index !== linkIndex),
    }));
  };

  return (
    <div className="flex flex-col gap-5">
      <ProjectLanguageSelector
        activeLanguageIndex={activeLanguageIndex}
        addDisabledTooltip={addDisabledTooltip}
        onAddLanguage={handleAddLanguage}
        onRemoveLanguage={handleRemoveLanguage}
        onSelectLanguage={setActiveIndex}
        onSetDefault={handleSetDefault}
        translations={project.translations}
      />

      <LabeledField id="project-title" label={t("titleLabel")}>
        <Input
          id="project-title"
          maxLength={128}
          value={activeTranslation.title ?? ""}
          onChange={(e): void =>
            onChangeTranslation("title", activeTranslation, e.target)
          }
        />
      </LabeledField>

      <LabeledField id="project-description" label={t("description")}>
        <textarea
          className="border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 min-h-20 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px]"
          id="project-description"
          value={activeTranslation.description ?? ""}
          onChange={(e): void =>
            onChangeTranslation("description", activeTranslation, e.target)
          }
        />
      </LabeledField>

      <LabeledField id="project-copyright" label={t("copyright")}>
        <Input
          id="project-copyright"
          maxLength={128}
          value={activeTranslation.copyright ?? ""}
          onChange={(e): void =>
            onChangeTranslation("copyright", activeTranslation, e.target)
          }
        />
      </LabeledField>

      <div className="flex flex-wrap gap-5">
        {IMAGE_FIELDS.map(({ attr, format, labelKey, getImageUrl }) => (
          <SimpleImageUpload
            key={`${attr}-${activeLanguage}`}
            format={format}
            id={`project-${attr}-${activeLanguage}`}
            label={t(labelKey)}
            onDelete={(): void => handleDeleteImage(attr)}
            rounded={false}
            onFileLoad={(result: string): void =>
              updateActiveTranslation({ [attr]: result })
            }
            value={resolveImageValue(
              activeTranslation[attr],
              getImageUrl(activeTranslation),
            )}
          />
        ))}
      </div>

      <div className="flex flex-col gap-3">
        <span className="text-foreground font-semibold">{t("links")}</span>
        {project.page_links.map((pageLink, index) => {
          const pageLinkTranslation = getPageLinkTranslation(
            pageLink,
            activeTranslation,
          );
          return (
            <div
              key={uuidv4()}
              className="bg-muted flex items-end gap-3 rounded-lg p-3"
            >
              <PageLinkField
                className="flex-1"
                id={`link-title-${index}`}
                label={t("linkTitle")}
                value={pageLinkTranslation.title}
                onChange={(e): void =>
                  handleChangePageLink("title", index, e.target.value)
                }
              />
              <PageLinkField
                className="flex-2"
                id={`link-url-${index}`}
                label={t("linkUrl")}
                value={pageLinkTranslation.url}
                action={
                  <Button
                    aria-label={`delete-link-${index}`}
                    className="cursor-pointer"
                    onClick={(): void => deletePageLink(index)}
                    size="xs"
                    type="button"
                    variant="destructive"
                  >
                    {t("removeLink")}
                  </Button>
                }
                onChange={(e): void =>
                  handleChangePageLink("url", index, e.target.value)
                }
              />
            </div>
          );
        })}
        {project.page_links.length < MAX_PAGE_LINKS ? (
          <Button
            className="cursor-pointer self-end"
            onClick={addPageLink}
            size="sm"
            type="button"
            variant="outline"
          >
            <Plus />
            {t("addLink")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default ProjectAdminTranslations;
